import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useGetPredictionProducts } from '@/api/prediction.queries';
import { mapProductsResponse } from '@/composables/usePredictionHub';
import type { PredictionFeaturedOffice } from '@/types/prediction.types';

/**
 * Election Hub "balance of power" strip (FTAPI-231): surfaces the Federal Race
 * Outcomes product alongside the office-specific control product (US House of
 * Representatives Control for `office: 'house'`, US Senate Majority for
 * `office: 'senate'`) via GET /prediction/product/featured?office={office}.
 *
 * Governor races have no balance-of-power product, so callers should only
 * invoke this with 'house' or 'senate' — pass undefined otherwise to disable
 * the query entirely.
 */
export const useElectionFeaturedProducts = (office: MaybeRefOrGetter<PredictionFeaturedOffice | undefined>) => {
  const params = computed(() => ({
    endpoint: 'featured' as const,
    office: toValue(office),
  }));

  const { data, isLoading, error } = useGetPredictionProducts(params, {
    enabled: () => !!toValue(office),
  });

  const markets = computed(() => {
    if (!toValue(office) || !data.value?.complex_products) return [];
    return mapProductsResponse(data.value.complex_products);
  });

  return { markets, isLoading, error };
};
// last modified by AI tools on 2026-10-09
